/**
 * 认证状态管理
 */

import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import type { UserProfile, RegisterRequest, LoginRequest, KdfConfig } from '@valuewarden/shared';
import { DEFAULT_KDF_CONFIG } from '@valuewarden/shared';
import { api, ApiError } from '../lib/api';
import {
  hashPassword,
  generateSalt,
  generateUserKey,
  exportKey,
  importKey,
  encryptData,
  decryptData,
  deriveEncryptionKey,
  generateRescueCode,
  normalizeRescueCode,
} from '../lib/crypto';
import {
  storeTokens,
  getStoredTokens,
  storeUser,
  getStoredUser,
  clearAll,
  isTokenExpiring,
} from '../lib/storage';

export const useAuthStore = defineStore('auth', () => {
  // 状态
  const user = ref<UserProfile | null>(null);
  const accessToken = ref<string | null>(null);
  const refreshToken = ref<string | null>(null);
  const isAuthenticated = ref(false);
  const isLoading = ref(false);
  const error = ref<string | null>(null);

  // 加密密钥（内存中，不持久化）
  const encryptionKey = ref<CryptoKey | null>(null);

  // 计算属性
  const isLoggedIn = computed(() => isAuthenticated.value && !!user.value);
  const isAdmin = computed(() => user.value?.isAdmin ?? false);

  // 初始化：从本地存储恢复
  function init() {
    const tokens = getStoredTokens();
    const storedUser = getStoredUser<UserProfile>();

    if (tokens && storedUser) {
      accessToken.value = tokens.accessToken;
      refreshToken.value = tokens.refreshToken;
      user.value = storedUser;
      isAuthenticated.value = true;
      api.setTokens(tokens.accessToken, tokens.refreshToken);
    }
  }

  // 预登录：获取 KDF 配置
  async function preLogin(email: string): Promise<{ kdf: KdfConfig; salt: string }> {
    try {
      const data = await api.preLogin(email);
      return {
        kdf: data.kdf,
        salt: data.passwordSalt || generateSalt(),
      };
    } catch (err) {
      // 即使用户不存在也返回默认配置
      return {
        kdf: DEFAULT_KDF_CONFIG,
        salt: generateSalt(),
      };
    }
  }

  // 注册（邀请制下普通用户需要 inviteToken，管理员邮箱可省略）
  // 返回值：一次性救援码（需调用方展示给用户保存）
  async function register(
    email: string,
    masterPassword: string,
    inviteToken?: string,
  ): Promise<string> {
    isLoading.value = true;
    error.value = null;

    try {
      // 1. 生成盐值和 KDF 配置
      const salt = generateSalt();
      const kdf = DEFAULT_KDF_CONFIG;

      // 2. 客户端哈希主密码
      const passwordHash = await hashPassword(masterPassword, salt, kdf);

      // 3. 生成用户密钥（用于加密密码库数据）
      const userKey = await generateUserKey();
      const userKeyBase64 = await exportKey(userKey);

      // 4. 用主密码派生的密钥加密用户密钥
      const masterEncryptionKey = await deriveEncryptionKey(masterPassword, salt, kdf);
      const { encryptedData: encryptedKey, iv, tag } = await encryptData(userKeyBase64, masterEncryptionKey);

      // 5. 组合加密的用户密钥（格式：iv:tag:encryptedData）
      const encryptedKeyCombined = `${iv}:${tag}:${encryptedKey}`;

      // 6. 生成救援码：哈希用于服务端验证，密钥用于加密用户密钥（主密码重置时恢复密码库）
      const rescueCode = generateRescueCode();
      const rescueSalt = generateSalt();
      const rescueHash = await hashPassword(rescueCode, rescueSalt, kdf);
      const rescueEncryptionKey = await deriveEncryptionKey(rescueCode, rescueSalt, kdf);
      const {
        encryptedData: encryptedKeyRescueData,
        iv: rescueIv,
        tag: rescueTag,
      } = await encryptData(userKeyBase64, rescueEncryptionKey);
      const encryptedKeyRescue = `${rescueIv}:${rescueTag}:${encryptedKeyRescueData}`;

      // 7. 发送注册请求
      const registerData: RegisterRequest = {
        email,
        passwordHash,
        passwordSalt: salt,
        kdf,
        encryptedKey: encryptedKeyCombined,
        inviteToken,
        rescueHash,
        rescueSalt,
        encryptedKeyRescue,
      };

      const result = await api.register(registerData);
      user.value = result.user;

      return rescueCode;
    } catch (err) {
      if (err instanceof ApiError) {
        error.value = err.message;
      } else {
        error.value = '注册失败，请稍后重试';
      }
      throw err;
    } finally {
      isLoading.value = false;
    }
  }

  // 登录
  async function login(email: string, masterPassword: string): Promise<void> {
    isLoading.value = true;
    error.value = null;

    try {
      // 1. 预登录获取 KDF 配置和盐值
      const { kdf, salt } = await preLogin(email);

      // 2. 客户端哈希主密码
      const passwordHash = await hashPassword(masterPassword, salt, kdf);

      // 3. 登录
      const loginData: LoginRequest = {
        email,
        passwordHash,
        device: {
          name: navigator.userAgent,
          type: 'web',
        },
      };

      const result = await api.login(loginData);

      // 4. 保存 Token 和用户信息
      accessToken.value = result.accessToken;
      refreshToken.value = result.refreshToken;
      user.value = result.user;
      isAuthenticated.value = true;

      api.setTokens(result.accessToken, result.refreshToken);

      const expiresAt = Date.now() + result.expiresIn * 1000;
      storeTokens({
        accessToken: result.accessToken,
        refreshToken: result.refreshToken,
        expiresAt,
      });
      storeUser(result.user);

      // 5. 用主密码派生的密钥解密用户密钥，作为密码库数据加密密钥（内存中）
      // 架构：vault 数据用随机用户密钥加密；主密码/救援码各自加密这把用户密钥
      const masterKey = await deriveEncryptionKey(masterPassword, salt, kdf);
      const [keyIv, keyTag, keyData] = result.user.encryptedKey.split(':');
      const userKeyBase64 = await decryptData(keyData, keyIv, keyTag, masterKey);
      encryptionKey.value = await importKey(userKeyBase64);
    } catch (err) {
      if (err instanceof ApiError) {
        error.value = err.message;
      } else {
        error.value = '登录失败，请检查邮箱和密码';
      }
      throw err;
    } finally {
      isLoading.value = false;
    }
  }

  // 刷新 Token
  async function refresh(): Promise<boolean> {
    try {
      if (!refreshToken.value) return false;

      const result = await api.refresh();
      accessToken.value = result.accessToken;
      refreshToken.value = result.refreshToken;
      api.setTokens(result.accessToken, result.refreshToken);

      const expiresAt = Date.now() + result.expiresIn * 1000;
      storeTokens({
        accessToken: result.accessToken,
        refreshToken: result.refreshToken,
        expiresAt,
      });

      return true;
    } catch {
      return false;
    }
  }

  // 登出
  async function logout(): Promise<void> {
    try {
      await api.logout();
    } catch {
      // 忽略登出错误
    }

    accessToken.value = null;
    refreshToken.value = null;
    user.value = null;
    isAuthenticated.value = false;
    encryptionKey.value = null;
    api.clearTokens();
    clearAll();
  }

  // 检查并刷新即将过期的 Token
  async function ensureValidToken(): Promise<boolean> {
    const tokens = getStoredTokens();
    if (!tokens) return false;

    if (isTokenExpiring(tokens)) {
      return await refresh();
    }

    return true;
  }

  // 忘记主密码：用救援码重置（无需登录）
  async function resetPassword(email: string, rescueCodeInput: string, newPassword: string): Promise<void> {
    isLoading.value = true;
    error.value = null;

    try {
      // 1. 预登录获取 KDF 配置和救援码加密的用户密钥
      const data = await api.preLogin(email);
      const rescueCode = normalizeRescueCode(rescueCodeInput);

      if (!data.rescueSalt || !data.encryptedKeyRescue) {
        throw new Error('该账号未设置救援码，无法重置主密码');
      }

      // 2. 用救援码派生密钥解出用户密钥（密码库数据不变，无需重新加密）
      const rescueKey = await deriveEncryptionKey(rescueCode, data.rescueSalt, data.kdf);
      const [rIv, rTag, rData] = data.encryptedKeyRescue.split(':');
      const userKeyBase64 = await decryptData(rData, rIv, rTag, rescueKey);

      // 3. 生成新主密码凭证
      const newPasswordSalt = generateSalt();
      const newPasswordHash = await hashPassword(newPassword, newPasswordSalt, data.kdf);
      const newMasterKey = await deriveEncryptionKey(newPassword, newPasswordSalt, data.kdf);
      const { encryptedData: newKeyData, iv: newIv, tag: newTag } = await encryptData(userKeyBase64, newMasterKey);
      const encryptedKey = `${newIv}:${newTag}:${newKeyData}`;

      // 4. 救援码哈希（服务端验证身份）
      const rescueHash = await hashPassword(rescueCode, data.rescueSalt, data.kdf);

      // 5. 提交重置（服务端验证通过后更新凭证并吊销全部会话）
      await api.resetPassword({
        email,
        rescueHash,
        passwordHash: newPasswordHash,
        passwordSalt: newPasswordSalt,
        kdf: data.kdf,
        encryptedKey,
      });
    } catch (err) {
      if (err instanceof ApiError) {
        error.value = err.message;
      } else {
        error.value = err instanceof Error ? err.message : '重置失败，请稍后重试';
      }
      throw err;
    } finally {
      isLoading.value = false;
    }
  }

  // 重新生成救援码（需登录，验证当前主密码）
  // 返回值：新的一次性救援码（需调用方展示给用户保存）
  async function setupRescueCode(masterPassword: string): Promise<string> {
    isLoading.value = true;
    error.value = null;

    try {
      if (!user.value) {
        throw new Error('未登录');
      }

      // 1. 预登录获取盐值和 KDF 配置
      const data = await api.preLogin(user.value.email);
      if (!data.passwordSalt) {
        throw new Error('无法获取账号信息');
      }

      // 2. 验证当前主密码：派生哈希（服务端比对）+ 解密用户密钥（需要它来生成救援码密文）
      const passwordHash = await hashPassword(masterPassword, data.passwordSalt, data.kdf);
      const masterKey = await deriveEncryptionKey(masterPassword, data.passwordSalt, data.kdf);
      const profile = await api.me();
      const [keyIv, keyTag, keyData] = profile.user.encryptedKey.split(':');
      const userKeyBase64 = await decryptData(keyData, keyIv, keyTag, masterKey);

      // 3. 生成新救援码：哈希 + 用救援码密钥加密用户密钥
      const rescueCode = generateRescueCode();
      const rescueSalt = generateSalt();
      const rescueHash = await hashPassword(rescueCode, rescueSalt, data.kdf);
      const rescueKey = await deriveEncryptionKey(rescueCode, rescueSalt, data.kdf);
      const {
        encryptedData: rescueKeyData,
        iv: rescueIv,
        tag: rescueTag,
      } = await encryptData(userKeyBase64, rescueKey);
      const encryptedKeyRescue = `${rescueIv}:${rescueTag}:${rescueKeyData}`;

      // 4. 提交（服务端验证主密码哈希后更新）
      await api.setRescueCode({
        passwordHash,
        rescueHash,
        rescueSalt,
        encryptedKeyRescue,
      });

      return rescueCode;
    } catch (err) {
      if (err instanceof ApiError) {
        error.value = err.message;
      } else {
        error.value = err instanceof Error ? err.message : '设置救援码失败';
      }
      throw err;
    } finally {
      isLoading.value = false;
    }
  }

  return {
    // 状态
    user,
    accessToken,
    isAuthenticated,
    isLoading,
    error,
    encryptionKey,

    // 计算属性
    isLoggedIn,
    isAdmin,

    // 方法
    init,
    preLogin,
    register,
    login,
    refresh,
    logout,
    ensureValidToken,
    resetPassword,
    setupRescueCode,
  };
});
